import { useAlert } from "../contexts/AlertContext";
import theme, { faunaTheme, themeMap } from "../theme";
import Resizer from "react-image-file-resizer";
import { AddressResponse, BuildingInfo, ReverseGeocodeRequest } from "../interfaces/Address";
import { AppName } from "./constants";
import { getTenantConfig } from "../tenantConfig";
export { AppName };

/** Returns the current tenant app name from the environment. */
export const getAppName = (): AppName =>
  (import.meta.env.VITE_APP_NAME as AppName) ?? AppName.FaunaProtect;

/** Returns the MUI theme object for the current tenant. */
export const getAppTheme = () => themeMap[getAppName()] ?? faunaTheme;

/** Returns the display name of the current tenant. */
export const getAppDisplayName = () => getTenantConfig(getAppName()).displayName;

export function isValidHttpUrl(str: string) {
  var pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
    "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
    "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
    "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // fragment locator
  return !!pattern.test(str);
}

export function getFileExtension(filename: string) {
  return filename.split(".").pop();
}

// 24 hours time validation
export const isTimeValid = (time: string) => {
  // Regular expression for 24-hour time format (HH:mm or HH,mm) with optional leading zero for hours
  const timeRegex = /^(0?[0-9]|1\d|2[0-3])([:,])([0-5]\d)$/;
  return timeRegex.test(time);
};

// 24 hours timecreation
// if we enter 1, make it to 01:00,
// convert to 4 digit
export function isValidNumberWithDot(value: number) {
  // Regular expression to check if the number has a dot or colon after one or two digits
  let regex = /^\d{1,2}([.,])\d*$/;
  // Check if the value matches the regex
  return regex.test(value.toString());
}

// display date as dd-mm-yy format
export function formatDate(originalDateString: string) {
  const originalDate = new Date(originalDateString);
  const day = originalDate.getDate().toString().padStart(2, "0");
  const month = (originalDate.getMonth() + 1).toString().padStart(2, "0");
  const year = originalDate.getFullYear();

  return `${day}-${month}-${year}`;
}

// export function formatClientName(name: string | null) {
//   const maxLength = 25;
//   if (name?.length > maxLength) {
//     return name.substring(0, maxLength) + "...";
//   }
//   return name;
// }

export function formatClientName(name: string | null): string {
  const maxLength = 25;

  // Handle null value
  if (name === null) {
    return "";
  }

  if (name.length > maxLength) {
    return name.substring(0, maxLength) + "...";
  }

  return name;
}

// Based on the API remark_status value set the container color
// remark_status = 2 : Red
// remark_status = 1||null : Blue
export const getViewStatusColor = (status: number) => {
  if (status === 2 || status === 3) {
    return theme.palette.info.main;
  } else if (status === 1 || status === null) {
    return theme.palette.info.light;
  }
};

// Based on the API remark_status value set the font color
// remark_status = 2 : white
// remark_status = 1||null : Black
export const getFontStatusColor = (status: number) => {
  if (status === 2 || status === 3) {
    return theme.palette.secondary.main;
  } else if (status === 1 || status === null) {
    return theme.palette.secondary.dark;
  }
};

// Check whether it is Monday to Sunday
export const isWeekday = (date: Date) => {
  return true;
};

// Convert the files to base64
export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export function getLongDateWithYear(dateString: string | null): string | null {
  if (dateString) {
    const date = new Date(dateString);
    const mm = date.getMonth() + 1; // getMonth() is zero-based
    const dd = date.getDate();

    return (
      (dd > 9 ? "" : "0") +
      dd +
      "-" +
      (mm > 9 ? "" : "0") +
      mm +
      "-" +
      date.getFullYear()
    );
  } else {
    return null;
  }
}

export const handleDownload = async (
  documentUrl: string,
  documentName: string | null,
  showAlert: (
    message: string,
    type: "error" | "success" | "info" | "warning"
  ) => void,
  t: (key: string) => string,
  setIsLoading: (loading: boolean) => void
) => {
  setIsLoading(true);

  if (!documentUrl || documentUrl.trim() === "") {
    showAlert(t("common.document_is_not_available"), "info");
    setIsLoading(false);
    return;
  }

  try {
    const response = await fetch(documentUrl);
    if (!response.ok) {
      showAlert(t("common.Download_Failed"), "error");
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", documentName ?? "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showAlert(t("common.PDF_Success_Alert"), "success");
  } catch (error) {
    showAlert(t("common.Download_Failed"), "error");
  } finally {
    setIsLoading(false);
  }
};

export const resizeImageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    Resizer.imageFileResizer(
      file, // The file to be resized
      1000, // Max width
      1000, // Max height
      "JPEG", // Output format
      60, // Quality (percentage)
      0, // Rotation
      (base64) => {
        if (typeof base64 == "string") resolve(base64); // Resolve with Base64 string
      },
      "base64" // Output type
    );
  });
};

export const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${hours.toString().padStart(2, '0')} : ${minutes.toString().padStart(2, '0')} : ${seconds.toString().padStart(2, '0')}`
}
export function delay(ms: number) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export const checkDateisToday = (logDate: string) => {
  const today = new Date()
  const logDateObj = new Date(logDate)

  return (
    today.getFullYear() === logDateObj.getFullYear() &&
    today.getMonth() === logDateObj.getMonth() &&
    today.getDate() === logDateObj.getDate()
  )
}

/**
 * Convert latitude and longitude coordinates to a formatted address using OpenStreetMap Nominatim API
 * @param request - Object containing lat, lon and optional parameters
 * @returns Promise<BuildingInfo | null> - Formatted address information or null if failed
 */
export const convertLatLonToAddress = async (
  request: ReverseGeocodeRequest
): Promise<BuildingInfo | null> => {
  try {
    const { lat, lon, centerid, centerlat, centerlon, manualTask = false, nodestopassdraw = [] } = request;

    const iconmarklat = lat;
    const iconmarklon = lon;

    // Make API call to OpenStreetMap Nominatim service
    const params = new URLSearchParams({
      format: 'jsonv2',
      lat: iconmarklat.toString(),
      lon: iconmarklon.toString(),
    });

    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`, {
      headers: {
        'User-Agent': 'FaunaFieldApp', // You can replace this with your app name
      },
    });

    if (!response.ok) {
      console.error('Nominatim API request failed:', response.statusText);
      return null;
    }

    const data: AddressResponse = await response.json();

    if (!data || !data.address) {
      console.error('No address data received from Nominatim API');
      return null;
    }

    const osmId = data.osm_id || null;
    let hno = '';

    // Determine house number or amenity
    if (data.address.house_number) {
      hno = data.address.house_number;
    } else if (data.address.amenity) {
      hno = data.address.amenity;
    } else {
      hno = '';
    }

    let houseNumber = hno;
    const finaladdress = data.address;

    // Determine city name with fallback logic
    let cityFinal = '';
    if (finaladdress.village) {
      cityFinal = finaladdress.city || finaladdress.village;
    } else {
      if (finaladdress.town) {
        cityFinal = finaladdress.town;
      } else {
        if (finaladdress.city) {
          cityFinal = finaladdress.city;
        } else if (finaladdress.municipality) {
          cityFinal = finaladdress.municipality;
        }
      }
    }

    const postcode = finaladdress.postcode || null;
    const quarter = finaladdress.quarter || null;
    const quarterfinal = quarter || finaladdress.neighbourhood || null;
    let road = finaladdress.road || null;
    const countryCode = finaladdress.country_code || null;
    const state = finaladdress.state || null;
    const country = finaladdress.country || null;

    // For manual tasks, clear road and house number
    if (manualTask) {
      road = null;
      houseNumber = '';
    }

    // Create building info object
    const buildingInfo: BuildingInfo = {
      lat: iconmarklat,
      lon: iconmarklon,
      house_number: houseNumber || null,
      municipality: cityFinal || null,
      postcode,
      quarter: quarterfinal,
      road,
      country,
      state,
      country_code: countryCode,
      type: data.type || null,
      addresstype: data.addresstype || null,
      osm_id: osmId,
      place_id: data.place_id || null,
      building_nodes: JSON.stringify(nodestopassdraw),
      center_id: centerid || null,
    };

    return buildingInfo;
  } catch (error) {
    console.error('Error converting coordinates to address:', error);
    return null;
  }
};

/**
 * Format a BuildingInfo object into a readable address string
 * @param buildingInfo - The building information object
 * @returns string - Formatted address string
 */
export const formatAddressString = (buildingInfo: BuildingInfo): string => {
  const parts: string[] = [];

  if (buildingInfo.road) {
    let roadPart = buildingInfo.road;
    if (buildingInfo.house_number) {
      roadPart += ` ${buildingInfo.house_number}`;
    }
    parts.push(roadPart);
  }

  if (buildingInfo.quarter) {
    parts.push(buildingInfo.quarter);
  }

  if (buildingInfo.municipality) {
    let cityPart = buildingInfo.municipality;
    if (buildingInfo.postcode) {
      cityPart = `${buildingInfo.postcode} ${cityPart}`;
    }
    parts.push(cityPart);
  }

  if (buildingInfo.country) {
    parts.push(buildingInfo.country);
  }

  return parts.join(', ');
};

// Function for dynamic favicon handling
export const getFaviconPath = (): string => {
  const appName = getAppName();
  const favicon = `/${getTenantConfig(appName).favicon}`;

  // Update the DOM link element
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  if (link) {
    link.href = favicon;
  }

  return favicon;
};

export const timeFormat = (timeString: string) => {
  if (!timeString) return '';
  // timeString is expected as 'HH:mm' or 'HH:mm:ss'
  const [hours, minutes] = timeString.split(':');
  const date = new Date();
  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));
  date.setSeconds(0);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export function parseEWKBHexToCoords(hex: string): { lat: number; lng: number } | null {
  if (!hex || hex.length < 48) return null;
  try {
    if (hex.startsWith('0x')) hex = hex.slice(2);
    const buf = new Uint8Array(hex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
    const littleEndian = buf[0] === 1;
    // EWKB Point with SRID: [byte 0: endianness][byte 1-4: type][byte 5-8: SRID][byte 9-16: X][byte 17-24: Y]
    const getFloat64 = (start: number) => {
      const arr = buf.slice(start, start + 8);
      const dv = new DataView(arr.buffer);
      return dv.getFloat64(0, littleEndian);
    };
    const mercatorX = getFloat64(9);
    const mercatorY = getFloat64(17);
    // Convert EPSG:3857 to EPSG:4326
    const lng = mercatorX * 180 / 20037508.34;
    const lat = (Math.atan(Math.exp(mercatorY * Math.PI / 20037508.34)) * 360 / Math.PI) - 90;
    return { lat, lng };
  } catch {
    return null;
  }
}
